import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  ChangeEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { Button, Spinner } from "reactstrap";
import { OPENAI_GET_TEMPLATES_LOADING } from "../../../constants/loading.constant";
import { useGlobalCore } from "../../../cores/globals";
import { useOpenAiCore } from "../../../cores/openAi";
import {
  MessageSchedulingChannel,
  MessageSchedulingTemplate,
  ScheduledMessageLink,
  ScheduledMessageMedia,
  extractFirstUrl,
  linkHostname,
  messageSchedulingId,
} from "../../utils/messageSchedulingStorage";
import { CheckboxWembii } from "../CheckboxWembii";
import { InputWembii } from "../InputWembii";
import { WhatsappMessagePreview } from "./WhatsappMessagePreview";

/** Data URLs superiores a esto rompen la cuota de localStorage, así que solo se guarda metadata. */
const MAX_PREVIEW_BYTES = 700_000;

type Panel = "none" | "link" | "ai" | "templates";

interface Props {
  companyId: string;
  channel: MessageSchedulingChannel;
  text: string;
  onTextChange: (value: string) => void;
  media: ScheduledMessageMedia[];
  onMediaChange: (media: ScheduledMessageMedia[]) => void;
  link?: ScheduledMessageLink;
  onLinkChange: (link?: ScheduledMessageLink) => void;
  templates: MessageSchedulingTemplate[];
  onSaveTemplate: (template: MessageSchedulingTemplate) => void;
  onDeleteTemplate: (id: string) => void;
  previewContactName?: string;
  previewContactPhone?: string;
  scheduledAt?: string;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export const MessageComposerStep = (props: Props) => {
  const {
    companyId,
    channel,
    text,
    onTextChange,
    media,
    onMediaChange,
    link,
    onLinkChange,
    templates,
    onSaveTemplate,
    onDeleteTemplate,
    previewContactName,
    previewContactPhone,
    scheduledAt,
  } = props;

  const intl = useIntl();
  const { isLoading } = useGlobalCore();
  const {
    templates: aiTemplates,
    getSocialTemplate,
    setShowTemplates,
  } = useOpenAiCore();

  const [panel, setPanel] = useState<Panel>("none");
  const [aiSubject, setAiSubject] = useState("");
  const [aiIndustry, setAiIndustry] = useState("");
  const [aiExtra, setAiExtra] = useState("");
  const [aiEmoticons, setAiEmoticons] = useState(true);
  const [aiRequested, setAiRequested] = useState(false);
  const [templateName, setTemplateName] = useState("");

  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);

  const aiLoading = isLoading(OPENAI_GET_TEMPLATES_LOADING);

  const channelLabel = useMemo(
    () =>
      intl.formatMessage({ id: `app.messageScheduling.channel.${channel}` }),
    [channel, intl]
  );

  const togglePanel = useCallback((next: Panel) => {
    setPanel((prev) => (prev === next ? "none" : next));
  }, []);

  const pickMedia = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      event.target.value = "";
      if (!files.length) return;

      const next: ScheduledMessageMedia[] = [];
      for (const file of files) {
        if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
          continue;
        }
        const entry: ScheduledMessageMedia = {
          id: messageSchedulingId(),
          name: file.name,
          type: file.type,
          size: file.size,
        };
        if (file.type.startsWith("image/") && file.size <= MAX_PREVIEW_BYTES) {
          try {
            entry.previewDataUrl = await readFileAsDataUrl(file);
          } catch {
            // Se conserva solo la metadata si falla la lectura.
          }
        }
        next.push(entry);
      }
      if (next.length) onMediaChange([...media, ...next]);
    },
    [media, onMediaChange]
  );

  const removeMedia = useCallback(
    (id: string) => onMediaChange(media.filter((item) => item.id !== id)),
    [media, onMediaChange]
  );

  const detectLinkFromText = useCallback(() => {
    const url = extractFirstUrl(text);
    if (!url) {
      toast.info(
        intl.formatMessage({ id: "app.messageScheduling.link.notFound" })
      );
      return;
    }
    onLinkChange({ ...link, url });
    setPanel("link");
  }, [intl, link, onLinkChange, text]);

  const onGenerate = useCallback(() => {
    if (!aiSubject.trim()) {
      toast.error(
        intl.formatMessage({ id: "app.messageScheduling.ai.subjectRequired" })
      );
      return;
    }
    setShowTemplates(true);
    setAiRequested(true);
    getSocialTemplate(companyId, {
      industry:
        aiIndustry.trim() ||
        intl.formatMessage({ id: "app.messageScheduling.ai.industryFallback" }),
      subject: aiSubject.trim(),
      emoticons: aiEmoticons,
      hashtags: false,
      additionalInfo: [
        intl.formatMessage(
          { id: "app.messageScheduling.ai.contextPrompt" },
          { channel: channelLabel }
        ),
        aiExtra.trim(),
      ]
        .filter(Boolean)
        .join(" "),
      socialMedias: [channelLabel],
    });
  }, [
    aiEmoticons,
    aiExtra,
    aiIndustry,
    aiSubject,
    channelLabel,
    companyId,
    getSocialTemplate,
    intl,
    setShowTemplates,
  ]);

  const applyTemplate = useCallback(
    (template: MessageSchedulingTemplate) => {
      onTextChange(template.text);
      onMediaChange(template.media || []);
      onLinkChange(template.link);
      toast.success(
        intl.formatMessage({ id: "app.messageScheduling.templates.applied" })
      );
    },
    [intl, onLinkChange, onMediaChange, onTextChange]
  );

  const saveTemplate = useCallback(() => {
    const name = templateName.trim();
    if (!name) {
      toast.error(
        intl.formatMessage({ id: "app.messageScheduling.templates.nameRequired" })
      );
      return;
    }
    if (!text.trim() && !media.length && !link?.url) {
      toast.error(
        intl.formatMessage({ id: "app.messageScheduling.message.required" })
      );
      return;
    }
    const existing = templates.find(
      (item) => item.name.toLowerCase() === name.toLowerCase()
    );
    onSaveTemplate({
      id: existing?.id || messageSchedulingId(),
      companyId,
      name,
      text,
      media,
      link,
      createdAt: existing?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setTemplateName("");
  }, [
    companyId,
    intl,
    link,
    media,
    onSaveTemplate,
    templateName,
    templates,
    text,
  ]);

  return (
    <div className="ms-composer">
      <div className="ms-composer__grid">
        <div className="ms-composer__main">
          <div className="ms-toolbar">
            <button
              type="button"
              className="ms-toolbar__btn"
              onClick={() => imageInputRef.current?.click()}
            >
              <FontAwesomeIcon icon={["fas", "image"]} />
              {intl.formatMessage({ id: "app.messageScheduling.message.images" })}
            </button>
            <button
              type="button"
              className="ms-toolbar__btn"
              onClick={() => videoInputRef.current?.click()}
            >
              <FontAwesomeIcon icon={["fas", "video"]} />
              {intl.formatMessage({ id: "app.messageScheduling.message.videos" })}
            </button>
            <button
              type="button"
              className={`ms-toolbar__btn ${
                panel === "link" ? "is-active" : ""
              }`}
              onClick={() => togglePanel("link")}
            >
              <FontAwesomeIcon icon={["fas", "link"]} />
              {intl.formatMessage({ id: "app.messageScheduling.message.link" })}
            </button>
            <div className="ms-toolbar__spacer" />
            <button
              type="button"
              className={`ms-toolbar__btn ms-toolbar__btn--ai ${
                panel === "ai" ? "is-active" : ""
              }`}
              onClick={() => togglePanel("ai")}
            >
              <FontAwesomeIcon icon={["fas", "wand-magic-sparkles"] as any} />
              {intl.formatMessage({ id: "app.messageScheduling.ai.button" })}
            </button>
            <button
              type="button"
              className={`ms-toolbar__btn ${
                panel === "templates" ? "is-active" : ""
              }`}
              onClick={() => togglePanel("templates")}
            >
              <FontAwesomeIcon icon={["far", "clone"]} />
              {intl.formatMessage({
                id: "app.messageScheduling.templates.button",
              })}
            </button>
          </div>

          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={pickMedia}
          />
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            multiple
            hidden
            onChange={pickMedia}
          />

          <div className="ms-field">
            <label className="ms-label">
              {intl.formatMessage({ id: "app.messageScheduling.message.text" })}
            </label>
            <textarea
              className="ms-textarea"
              rows={7}
              value={text}
              placeholder={intl.formatMessage({
                id: "app.messageScheduling.message.textPlaceholder",
              })}
              onChange={(event) => onTextChange(event.target.value)}
            />
            <div className="ms-field__footer">
              <span>{text.length} / 4096</span>
              <button
                type="button"
                className="ms-linkish"
                onClick={detectLinkFromText}
              >
                <FontAwesomeIcon icon={["fas", "magnifying-glass"] as any} />
                {intl.formatMessage({
                  id: "app.messageScheduling.link.detect",
                })}
              </button>
            </div>
          </div>

          {panel === "link" && (
            <div className="ms-subpanel">
              <div className="ms-subpanel__title">
                <FontAwesomeIcon icon={["fas", "link"]} />
                {intl.formatMessage({
                  id: "app.messageScheduling.link.title",
                })}
              </div>
              <InputWembii
                type="text"
                label={intl.formatMessage({
                  id: "app.messageScheduling.link.url",
                })}
                placeholder="https://"
                value={link?.url || ""}
                onChange={(value: string) =>
                  onLinkChange(value ? { ...link, url: value } : undefined)
                }
              />
              <InputWembii
                type="text"
                label={intl.formatMessage({
                  id: "app.messageScheduling.link.linkTitle",
                })}
                value={link?.title || ""}
                disabled={!link?.url}
                onChange={(value: string) =>
                  onLinkChange({ url: link?.url || "", ...link, title: value })
                }
              />
              <InputWembii
                type="text"
                label={intl.formatMessage({
                  id: "app.messageScheduling.link.description",
                })}
                value={link?.description || ""}
                disabled={!link?.url}
                onChange={(value: string) =>
                  onLinkChange({
                    url: link?.url || "",
                    ...link,
                    description: value,
                  })
                }
              />
              {link?.url && (
                <div className="ms-subpanel__actions">
                  <span className="ms-chip">{linkHostname(link.url)}</span>
                  <Button
                    color="link"
                    className="text-danger p-0"
                    onClick={() => onLinkChange(undefined)}
                  >
                    {intl.formatMessage({
                      id: "app.messageScheduling.link.remove",
                    })}
                  </Button>
                </div>
              )}
            </div>
          )}

          {panel === "ai" && (
            <div className="ms-subpanel ms-subpanel--ai">
              <div className="ms-subpanel__title">
                <FontAwesomeIcon icon={["fas", "wand-magic-sparkles"] as any} />
                {intl.formatMessage({ id: "app.messageScheduling.ai.title" })}
              </div>
              <p className="ms-subpanel__hint">
                {intl.formatMessage({ id: "app.messageScheduling.ai.hint" })}
              </p>

              <InputWembii
                type="text"
                label={intl.formatMessage({
                  id: "app.messageScheduling.ai.subject",
                })}
                placeholder={intl.formatMessage({
                  id: "app.messageScheduling.ai.subjectPlaceholder",
                })}
                value={aiSubject}
                onChange={setAiSubject}
              />
              <InputWembii
                type="text"
                label={intl.formatMessage({
                  id: "app.messageScheduling.ai.industry",
                })}
                placeholder={intl.formatMessage({
                  id: "app.messageScheduling.ai.industryPlaceholder",
                })}
                value={aiIndustry}
                onChange={setAiIndustry}
              />
              <InputWembii
                type="textarea"
                rows={3}
                label={intl.formatMessage({
                  id: "app.messageScheduling.ai.extra",
                })}
                placeholder={intl.formatMessage({
                  id: "app.messageScheduling.ai.extraPlaceholder",
                })}
                value={aiExtra}
                onChange={setAiExtra}
              />
              <CheckboxWembii
                label={intl.formatMessage({
                  id: "app.messageScheduling.ai.emoticons",
                })}
                checked={aiEmoticons}
                onChange={() => setAiEmoticons((prev) => !prev)}
              />

              <Button
                className="ms-btn ms-btn--ai mt-2"
                disabled={aiLoading}
                onClick={onGenerate}
              >
                {aiLoading ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <FontAwesomeIcon
                    icon={["fas", "wand-magic-sparkles"] as any}
                    className="mr-2"
                  />
                )}
                {intl.formatMessage({ id: "app.messageScheduling.ai.generate" })}
              </Button>

              {aiRequested && !aiLoading && (
                <div className="ms-aiResults">
                  {aiTemplates.length === 0 ? (
                    <div className="ms-empty ms-empty--sm">
                      {intl.formatMessage({
                        id: "app.messageScheduling.ai.noResults",
                      })}
                    </div>
                  ) : (
                    aiTemplates.map((option: any, index: number) => (
                      <div
                        key={option.id || `${option.title}-${index}`}
                        className="ms-aiResult"
                      >
                        <div className="ms-aiResult__title">
                          {option.title ||
                            intl.formatMessage(
                              { id: "app.messageScheduling.ai.option" },
                              { index: index + 1 }
                            )}
                        </div>
                        <div className="ms-aiResult__body">{option.body}</div>
                        <div className="ms-aiResult__actions">
                          <Button
                            color="link"
                            className="p-0 mr-3"
                            onClick={() => onTextChange(option.body)}
                          >
                            {intl.formatMessage({
                              id: "app.messageScheduling.ai.use",
                            })}
                          </Button>
                          <Button
                            color="link"
                            className="p-0"
                            onClick={() =>
                              onTextChange(
                                text ? `${text}\n\n${option.body}` : option.body
                              )
                            }
                          >
                            {intl.formatMessage({
                              id: "app.messageScheduling.ai.append",
                            })}
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {panel === "templates" && (
            <div className="ms-subpanel">
              <div className="ms-subpanel__title">
                <FontAwesomeIcon icon={["far", "clone"]} />
                {intl.formatMessage({
                  id: "app.messageScheduling.templates.title",
                })}
              </div>
              <p className="ms-subpanel__hint">
                {intl.formatMessage({
                  id: "app.messageScheduling.templates.hint",
                })}
              </p>

              <div className="ms-templateSave">
                <InputWembii
                  type="text"
                  mb={false}
                  className="flex-grow-1"
                  placeholder={intl.formatMessage({
                    id: "app.messageScheduling.templates.namePlaceholder",
                  })}
                  value={templateName}
                  onChange={setTemplateName}
                />
                <Button className="ms-btn ms-btn--soft" onClick={saveTemplate}>
                  <FontAwesomeIcon icon={["fas", "floppy-disk"] as any} className="mr-2" />
                  {intl.formatMessage({
                    id: "app.messageScheduling.templates.save",
                  })}
                </Button>
              </div>

              {templates.length === 0 ? (
                <div className="ms-empty ms-empty--sm">
                  {intl.formatMessage({
                    id: "app.messageScheduling.templates.empty",
                  })}
                </div>
              ) : (
                <div className="ms-templateList">
                  {templates.map((template) => (
                    <div key={template.id} className="ms-templateCard">
                      <div className="ms-templateCard__main">
                        <strong>{template.name}</strong>
                        <span>
                          {template.text.slice(0, 120) ||
                            intl.formatMessage({
                              id: "app.messageScheduling.templates.noText",
                            })}
                        </span>
                        <div className="ms-templateCard__meta">
                          {template.media.length > 0 && (
                            <span className="ms-chip ms-chip--sm">
                              <FontAwesomeIcon icon={["fas", "paperclip"]} />{" "}
                              {template.media.length}
                            </span>
                          )}
                          {template.link?.url && (
                            <span className="ms-chip ms-chip--sm">
                              <FontAwesomeIcon icon={["fas", "link"]} />{" "}
                              {linkHostname(template.link.url)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="ms-templateCard__actions">
                        <Button
                          color="link"
                          className="p-0"
                          onClick={() => applyTemplate(template)}
                        >
                          {intl.formatMessage({
                            id: "app.messageScheduling.templates.apply",
                          })}
                        </Button>
                        <Button
                          color="link"
                          className="p-0 text-danger"
                          onClick={() => onDeleteTemplate(template.id)}
                        >
                          {intl.formatMessage({
                            id: "app.messageScheduling.delete",
                          })}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {media.length > 0 && (
            <div className="ms-mediaGrid">
              {media.map((file) => (
                <div key={file.id} className="ms-mediaGrid__item">
                  {file.previewDataUrl ? (
                    <img src={file.previewDataUrl} alt={file.name} />
                  ) : (
                    <div className="ms-mediaGrid__placeholder">
                      <FontAwesomeIcon
                        icon={[
                          "fas",
                          file.type.startsWith("video/") ? "video" : "image",
                        ]}
                      />
                    </div>
                  )}
                  <div className="ms-mediaGrid__name">{file.name}</div>
                  <button
                    type="button"
                    className="ms-mediaGrid__remove"
                    aria-label="remove"
                    onClick={() => removeMedia(file.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="ms-composer__preview">
          <div className="ms-composer__previewTitle">
            {intl.formatMessage({ id: "app.messageScheduling.preview.title" })}
          </div>
          <WhatsappMessagePreview
            channel={channel}
            text={text}
            media={media}
            link={link}
            contactName={previewContactName}
            contactPhone={previewContactPhone}
            scheduledAt={scheduledAt}
          />
        </div>
      </div>
    </div>
  );
};
